import { useEffect, useState } from 'react'

import { useRouter } from 'next/router'

import { useInPageNavContext } from '~/contexts/InPageNavContext'
import { getRouterPath } from '~/utils/getRouterPath'

import type { filtersType } from '../ArtworksList/ArtworksList'

import type { FiltersProps } from './Filters'

const useHooks = (
  artists: FiltersProps['artists'],
  genres: FiltersProps['genres'],
  prices: FiltersProps['genres'],
  formats: FiltersProps['formats'],
  filters: filtersType,
  clearSearchTerm: FiltersProps['clearSearchTerm'],
  initialFilters?: Partial<filtersType>
) => {
  const [isSlideOutOpen, setIsSlideOutOpen] = useState<boolean>(false)
  const [activeFilterType, setActiveFilterType] = useState<string>()
  const [checkedGenres, setCheckedGenres] = useState<any>([])
  const [checkedArtists, setCheckedArtists] = useState<any>([])
  const [checkedFormats, setCheckedFormats] = useState<any>([])
  const [selectedPrices, setSelectedPrices] = useState<any>([])
  const [genresInit, setGenresInit] = useState<boolean>(false)
  const [artistsInit, setArtistsInit] = useState<boolean>(false)
  const [formatsInit, setFormatsInit] = useState<boolean>(false)
  const [pricesInit, setPricesInit] = useState<boolean>(false)
  const { setClickedInPageNav } = useInPageNavContext()
  const router = useRouter()

  const updateUrl = (params?: any, stripPage?: boolean) => {
    const isArtworkPage = router.asPath.indexOf('artworks') !== -1

    const page = getRouterPath(router).split('/').pop()

    if (stripPage) {
      delete router.query.p
    }
    delete router.query['lang']

    // Build the query object first
    const query = {
      ...router.query,
      ...params,
    }

    // Remove any null/undefined values from the query
    if (params) {
      Object.keys(params).forEach(param => {
        if (
          params[param] === null ||
          params[param] === undefined ||
          !params[param]
        ) {
          delete query[param]
        }
      })
    }

    if (page) {
      query.slug = page
    }

    const path = params
      ? {
          pathname: getRouterPath(router),
          query: query,
        }
      : {
          pathname: getRouterPath(router),
        }

    // Build artistPath query without null values
    const artistQuery = { ...query }

    const artistPath = params
      ? {
          pathname: getRouterPath(router),
          query: artistQuery,
        }
      : {
          pathname: getRouterPath(router),
          query: { slug: page },
        }

    setClickedInPageNav(false)
    // need a check for artworks
    router.push(isArtworkPage ? path : artistPath, undefined, { shallow: true })
  }

  // Create some arrays filled with false for the landing state of the checkboxes
  const initGenreCheckedState = () => {
    if (genres) {
      setCheckedGenres(
        genres.map(genre =>
          filters.genres.indexOf(genre._id) !== -1
            ? { id: genre._id, checked: true }
            : { id: genre._id, checked: false }
        )
      )
      setGenresInit(true)
    }
  }

  const initArtistCheckedState = () => {
    if (artists) {
      const selectedArtists = artists?.map(artist =>
        filters.artists.indexOf(artist._id) !== -1
          ? { id: artist._id, checked: true }
          : { id: artist._id, checked: false }
      )

      setCheckedArtists([...selectedArtists])
      setArtistsInit(true)
    }
  }

  const initPricesSelectedState = () => {
    if (prices) {
      setSelectedPrices([])
      setPricesInit(true)
    }
  }

  const initFormatCheckedState = () => {
    if (formats) {
      setCheckedFormats(
        formats?.map(format =>
          filters.formats.indexOf(format._id) !== -1
            ? { id: format._id, checked: true }
            : { id: format._id, checked: false }
        )
      )
      setFormatsInit(true)
    }
  }
  useEffect(() => {
    setGenresInit(false)
  }, [genres])
  useEffect(() => {
    setFormatsInit(false)
  }, [formats])
  useEffect(() => {
    setPricesInit(false)
  }, [prices])
  useEffect(() => {
    setArtistsInit(false)
  }, [artists])

  if (!genresInit) initGenreCheckedState()
  if (!artistsInit) initArtistCheckedState()
  if (!formatsInit) initFormatCheckedState()
  if (!pricesInit) initPricesSelectedState()
  if (!artistsInit) initArtistCheckedState()

  // determine if any filters have changed - will trigger the slideout modal to close if so
  useEffect(() => {
    let hasChanged = 0

    if (filters.genres?.length > 0) {
      // Check the ones in the array who's slug match?
      const checkGenresHolder = checkedGenres?.map((genre: any) => {
        return { ...genre, checked: filters.genres.includes(genre.id) }
      })

      if (JSON.stringify(checkedGenres) !== JSON.stringify(checkGenresHolder))
        hasChanged++

      setCheckedGenres(checkGenresHolder)
    }
    if (filters.artists?.length > 0) {
      // Check the ones in the array who's slug match?
      const checkArtistsHolder = checkedArtists?.map((artist: any) => {
        return { ...artist, checked: filters.artists.includes(artist.id) }
      })
      if (JSON.stringify(checkedArtists) === JSON.stringify(checkArtistsHolder))
        hasChanged++
      setCheckedArtists(checkArtistsHolder)
    }
    if (filters.formats?.length > 0) {
      // Check the ones in the array who's slug match?
      const checkFormatsHolder = checkedFormats?.map((format: any) => {
        return { ...format, checked: filters.formats.includes(format.id) }
      })
      if (JSON.stringify(checkedFormats) === JSON.stringify(checkFormatsHolder))
        hasChanged++
      setCheckedFormats(checkFormatsHolder)
    }

    if (filters.prices?.length > 0) {
      // Check the ones in the array who's slug match?
      if (JSON.stringify(selectedPrices) === JSON.stringify(filters.prices))
        hasChanged++
      setSelectedPrices(selectedPrices)
    }

    if (hasChanged > 0) closeFilters()
  }, [filters])

  const getFilterStateAndIds = (
    id: string,
    subjectArray: string[],
    filterSubject: any
  ) => {
    const updatedCheckedState = subjectArray.map((item: any) => {
      return item.id === id ? { ...item, checked: !item.checked } : item
    }) as string[]

    const updatedIds = getIDsArray(updatedCheckedState, filterSubject)
    return { updatedIds, itemsArr: updatedCheckedState }
  }

  const getIDsArray = (arr: any, compare: any) => {
    return arr.reduce((ids: string[], item: any) => {
      if (item.checked && compare) {
        ids.push(item.id)
      }
      return ids
    }, []) as string[]
  }

  const [filtersRef, setFiltersRef] = useState<any>()
  useEffect(() => {
    if (JSON.stringify(filtersRef) !== JSON.stringify(filters) || !filtersRef) {
      setFiltersRef(filters)
    }
  }, [filters])

  const updateGenresHandler = (id: string) => {
    const { updatedIds, itemsArr } = getFilterStateAndIds(
      id,
      checkedGenres,
      genres
    )

    setCheckedGenres(itemsArr)

    updateUrl({ genres: JSON.stringify(updatedIds) }, true)
  }

  const updateArtistHandler = (id: string) => {
    // const { updatedIds, itemsArr } = getFilterStateAndIds(
    //   id,
    //   checkedArtists,
    //   artists
    // )
    const updatedCheckedState = checkedArtists.map((item: any) => {
      return item.id === id ? { ...item, checked: !item.checked } : item
    }) as string[]

    setCheckedArtists([...updatedCheckedState])
    updateUrl({ artists: JSON.stringify([id]) }, true)
  }

  const updateFormatsHandler = (id: string) => {
    const { updatedIds, itemsArr } = getFilterStateAndIds(
      id,
      checkedFormats,
      formats
    )
    setCheckedFormats(itemsArr)
    updateUrl({ formats: JSON.stringify(updatedIds) }, true)
  }

  const updatePricesHandler = (range: [number?, number?]) => {
    setSelectedPrices(range)
    updateUrl({ prices: JSON.stringify(range) }, true)
  }

  const handleTradingFilter = (type: filtersType['tradingFloor']) => {
    if (filters.tradingFloor === type) {
      updateUrl({ 'trading-floor': null, p: null, tradingView: 1 })
      return
    }
    updateUrl({ 'trading-floor': type, p: null, tradingView: 1 })
  }

  const openFilter = (type: string) => {
    setActiveFilterType(type)
    setIsSlideOutOpen(true)
    setClickedInPageNav(true)
  }

  const closeFilters = () => {
    setIsSlideOutOpen(false)
    setClickedInPageNav(false)
  }

  const clearGenreFilters = () => {
    initGenreCheckedState()
    updateUrl({ genres: null })
    setClickedInPageNav(false)
  }

  const clearFormatFilters = () => {
    initFormatCheckedState()
    updateUrl({ formats: null })
    setClickedInPageNav(false)
  }

  const clearArtistFilters = () => {
    initArtistCheckedState()
    updateUrl({ artists: null })
    setClickedInPageNav(false)
  }

  const clearAllFilters = () => {
    initGenreCheckedState()
    initFormatCheckedState()
    initArtistCheckedState()
    initPricesSelectedState()

    clearArtistFilters()
    clearSearchTerm?.()

    updateUrl({
      tradingView: router.query.tradingView ? 1 : null,
      'trading-floor': null,
      genres: null,
      formats: null,
      artists: null,
      listPriceMin: null,
      prices: null,
    })
  }

  const getTitle = () => {
    switch (activeFilterType) {
      case 'artist':
        return 'Artist'
      case 'genre':
        return 'Genre'
      case 'format':
        return 'Format'
      case 'listPriceMin':
        return 'Price'
      case 'viewAll':
        return 'All Artworks'
    }
  }

  return {
    openFilter,
    isSlideOutOpen,
    clearAllFilters,
    clearFormatFilters,
    clearGenreFilters,
    getTitle,
    updatePricesHandler,
    updateGenresHandler,
    updateFormatsHandler,
    closeFilters,
    activeFilterType,
    checkedGenres,
    checkedFormats,
    selectedPrices,
    handleTradingFilter,
    tradingView: router.query.tradingView,
    updateUrl,
    updateArtistHandler,
    clearArtistFilters,
    checkedArtists,
  }
}

export default useHooks
